import { recoveryPrefix } from '../../../../packages/shared/browser-recovery/storage';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  createTransferCommands,
  transferAccountKey,
  transferRequest,
  type TransferCommands,
} from '../../../../packages/shared/transfers/client';
import { useAuth } from '../../App';
const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';
const commands = new Map<string, TransferCommands>();
window.addEventListener('storage', () => commands.forEach(command => command.refreshSaved()));
window.addEventListener('beforeunload', (event) => {
  if ([...commands.values()].some((value) => value.snapshot().pending)) {
    event.preventDefault();
    event.returnValue = '';
  }
});
export function useTransferClient() {
  const { user, currentShop } = useAuth();
  const account = useMemo(
    () => ({
      userId: String(user?.id || ''),
      domain: currentShop?.domain || '',
      organizationId: currentShop?.organizationId,
    }),
    [user?.id, currentShop?.domain, currentShop?.organizationId],
  );
  const key = transferAccountKey(account);
  if (!commands.has(key)) commands.set(key, createTransferCommands(API_URL, account));
  const command = commands.get(key)!;
  const state = useSyncExternalStore(command.subscribe, command.snapshot);
  const read = useMemo(
    () =>
      <T>(path: string, signal?: AbortSignal) =>
        transferRequest<T>(API_URL, account, path, { signal }),
    [account],
  );
  return { key, draftKey: recoveryPrefix('draft', API_URL, account), read, ...state, locked: state.busy || !!state.pending || state.blocked, submit: command.submit, retry: command.retry };
}
export type TransferClient = ReturnType<typeof useTransferClient>;
export function useTransferRead<T>(client: TransferClient, path: string, refresh = 0) {
  const key = client.key + path;
  // Starting a new command clears its old success banner, but must not unmount the draft.
  const lastConfirmed = useRef(client.success);
  if (client.success) lastConfirmed.current = client.success;
  const confirmed = lastConfirmed.current;
  const [result, setResult] = useState<{ key: string; data: T | null; error: string }>({ key, data: null, error: '' });
  useEffect(() => {
    const controller = new AbortController();
    setResult({ key, data: null, error: '' });
    void client
      .read<T>(path, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setResult({ key, data, error: '' });
      })
      .catch((error) => {
        if (!controller.signal.aborted) setResult({ key, data: null, error: error.message });
      });
    return () => controller.abort();
  }, [key, path, client.read, confirmed, refresh]);
  return result.key === key ? result : { data: null, error: '' };
}
